import React, { useEffect, useMemo } from 'react';
import { Form, Row, Col, Input, Select, DatePicker, Upload, Button, Space, Statistic, Card, Typography } from 'antd';
import { UserOutlined, BookOutlined, CalendarOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fileApi } from '@/services/api';
import { sanitizeSlug } from '@/utils/slug';
import { formatNumber } from '@/utils/number';

interface AcademyDetail {
  id: string;
  academyName: string;
  representativeName: string;
  representativePhone: string;
  representativeEmail: string;
  businessNumber: string;
  academyCode: string;
  totalUsers: number;
  activeUsers: number;
  teacherCount: number;
  academyUrl: string;
  logo: string;
  seatRange?: string;
  registrationStatus: string;
  serviceFeePayment: string;
  serviceFeePaymentDate: string;
  contractStatus: string;
  contractStatusKey?: string;
  contractStatusLabel?: string;
  overdueCount?: number;
  overdueAmount?: number;
  applicationDate: string;
  registrationDate: string;
  withdrawalDate?: string;
}

interface BasicInfoProps {
  academyData: AcademyDetail;
}

const ACADEMY_DOMAIN = (process.env.REACT_APP_ACADEMY_HOST || 'https://alphaca.mvpick.net').replace(/\/+$/, '');

const extractSlugFromUrl = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const parsed = trimmed.includes('://') ? new URL(trimmed) : new URL(`${ACADEMY_DOMAIN}/${trimmed}`);
    const pathSlug = parsed.pathname.replace(/^\/+/, '').split('/')[0] || '';
    return sanitizeSlug(pathSlug);
  } catch {
    return sanitizeSlug(trimmed);
  }
};

const formatSeatRange = (value?: string) => {
  if (!value) return '-';
  const normalized = value.trim();
  const predefined: Record<string, string> = {
    '1-25': '1 ~ 25명',
    '26-50': '26 ~ 50명',
    '51-75': '51 ~ 75명',
    '76-100': '76 ~ 100명',
    '101-125': '101 ~ 125명',
    '126-150': '126 ~ 150명',
    '151-175': '151 ~ 175명',
    '176-200': '176 ~ 200명',
    '200+': '200명 이상',
  };
  if (predefined[normalized]) return predefined[normalized];
  if (normalized.endsWith('+')) {
    return `${normalized.replace('+', '')}명 이상`;
  }
  if (normalized.includes('-')) {
    const [from, to] = normalized.split('-');
    if (from && to) {
      return `${from} ~ ${to}명`;
    }
  }
  return `${normalized}명`;
};

const CONTRACT_STATUS_CONFIG: Record<string, { text: string; color: string }> = {
  free_usage: { text: '무료사용', color: '#1890ff' },
  payment_waiting: { text: '결제대기', color: '#fa8c16' },
  payment_active: { text: '결제중', color: '#52c41a' },
  payment_failed: { text: '미납', color: '#f5222d' },
  billing_hold: { text: '유예기간', color: '#722ed1' },
  cancelled: { text: '해지', color: '#8c8c8c' },
};

const formatCurrency = (value?: number) => `${formatNumber(value ?? 0)}원`;

const getContractStatusDisplay = (academyData: AcademyDetail) => {
  const key = academyData.contractStatusKey || academyData.contractStatus?.toLowerCase() || '';
  const config = key ? CONTRACT_STATUS_CONFIG[key] : undefined;
  let label = academyData.contractStatusLabel || config?.text || academyData.contractStatus || '-';
  if (key === 'payment_failed' && (academyData.overdueCount || academyData.overdueAmount)) {
    label = `${label} (${formatNumber(academyData.overdueCount || 0)}회 / ${formatCurrency(academyData.overdueAmount || 0)})`;
  }
  return {
    label,
    color: config?.color || '#595959',
  };
};

const { Text } = Typography;

const BasicInfo: React.FC<BasicInfoProps> = ({ academyData }) => {
  const [form] = Form.useForm();
  const logoList = Form.useWatch('logo', form);
  const serviceFeePayment = Form.useWatch('serviceFeePayment', form);
  const seatRangeDisplay = formatSeatRange(academyData.seatRange);
  const contractStatusDisplay = getContractStatusDisplay(academyData);
  const isPaymentDateLocked = Boolean(academyData.serviceFeePaymentDate);
  const today = useMemo(() => dayjs().startOf('day'), []);
  const onlyTodayDisabledDate = (current: dayjs.Dayjs) => !current || !current.isSame(today, 'day');

  useEffect(() => {
    if (serviceFeePayment === 'paid' && !isPaymentDateLocked) {
      const currentValue = form.getFieldValue('serviceFeePaymentDate');
      const currentDayjs = currentValue ? dayjs(currentValue) : null;
      if (!currentDayjs || !currentDayjs.isSame(today, 'day')) {
        form.setFieldsValue({ serviceFeePaymentDate: today });
      }
    }
  }, [form, isPaymentDateLocked, serviceFeePayment, today]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      const logoUrlFieldValue = form.getFieldValue('logoUrl');
      let logoUrlValue: string | undefined;
      if (logoUrlFieldValue !== undefined) {
        logoUrlValue = logoUrlFieldValue;
      } else if (Array.isArray(values.logo)) {
        logoUrlValue = values.logo[0]?.url || '';
      } else if (values.logo) {
        logoUrlValue = values.logo;
      } else {
        logoUrlValue = academyData.logo || '';
      }
      // 날짜 값을 문자열로 변환하여 상위로 전달
      const normalized = {
        ...values,
        logoUrl: logoUrlValue,
        academyUrl: academyData.academyUrl,
        serviceFeePaymentDate: values.serviceFeePaymentDate ? values.serviceFeePaymentDate.format('YYYY-MM-DD') : undefined,
        applicationDate: academyData.applicationDate || undefined,
        registrationDate: academyData.registrationDate || undefined,
      };
      const event = new CustomEvent('admin:academy:save-basic', { detail: normalized });
      window.dispatchEvent(event);
    });
  };

  const handleWithdraw = () => {
    const event = new CustomEvent('admin:academy:withdraw');
    window.dispatchEvent(event);
  };

  const initialLogoList = academyData.logo
    ? [{ uid: '-1', name: 'logo', status: 'done' as const, url: academyData.logo }]
    : [];
  const initialPaymentDate = academyData.serviceFeePaymentDate
    ? dayjs(academyData.serviceFeePaymentDate)
    : academyData.serviceFeePayment === 'paid'
      ? today
      : undefined;

  return (
    <div>
      {/* 통계 카드 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="실 사용인원 / 총 사용인원"
              value={`${academyData.activeUsers} / ${academyData.totalUsers}`}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="강사 수"
              value={academyData.teacherCount ?? 0}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="가입상태"
              value={
                academyData.registrationStatus === 'APPROVED' ? '승인' :
                academyData.registrationStatus === 'PENDING' ? '승인대기' :
                academyData.registrationStatus === 'REJECTED' ? '반려' :
                academyData.registrationStatus === 'WITHDRAWN' ? '탈퇴' : (academyData.registrationStatus || '-')
              }
              prefix={<CalendarOutlined />}
              valueStyle={{ 
                color: academyData.registrationStatus === 'APPROVED' ? '#52c41a' : 
                       academyData.registrationStatus === 'PENDING' ? '#faad14' : 
                       academyData.registrationStatus === 'REJECTED' ? '#ff4d4f' : '#8c8c8c'
              }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="계약상태"
              value={contractStatusDisplay.label}
              prefix={<CalendarOutlined />}
              valueStyle={{ 
                color: contractStatusDisplay.color
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* 기본 정보 폼 */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...academyData,
          academyUrl: extractSlugFromUrl(academyData.academyUrl),
          serviceFeePaymentDate: initialPaymentDate,
          applicationDate: academyData.applicationDate ? dayjs(academyData.applicationDate) : undefined,
          registrationDate: academyData.registrationDate ? dayjs(academyData.registrationDate) : undefined,
          withdrawalDate: academyData.withdrawalDate ? dayjs(academyData.withdrawalDate) : undefined,
          logo: initialLogoList,
          logoUrl: academyData.logo || '',
        }}
      >
        <Form.Item name="logoUrl" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="ID" name="id">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="학원명" name="academyName">
              <Input maxLength={100} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="대표자명" name="representativeName">
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              label="대표자 연락처" 
              name="representativePhone"
              normalize={(v) => (v || '').replace(/\D/g, '')}
            >
              <Input maxLength={30} inputMode="numeric" pattern="[0-9]*" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="대표자 이메일" name="representativeEmail">
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="사업자 등록번호" name="businessNumber">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="학원코드" name="academyCode">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="학원URL"
              name="academyUrl"
              normalize={(value) => sanitizeSlug(value || '')}
            >
              <Input addonBefore={`${ACADEMY_DOMAIN}/`} placeholder="academy-slug" disabled maxLength={200} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="사용인원">
              <Input disabled value={seatRangeDisplay} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="로고 이미지" name="logo" valuePropName="fileList" getValueFromEvent={(e)=> Array.isArray(e?.fileList) ? e.fileList : []}>
              <Upload
                listType="picture-card"
                maxCount={1}
                onRemove={() => {
                  form.setFieldsValue({ logo: [], logoUrl: '' });
                  return true;
                }}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    const res = await fileApi.upload(file as File, 'academy/logo');
                    const url = (res && res.data && res.data.url) ? res.data.url : (res && res.url ? res.url : '');
                    form.setFieldsValue({
                      logo: [
                        {
                          uid: (file as any)?.uid || String(Date.now()),
                          name: (file as File).name,
                          status: 'done',
                          url,
                        },
                      ],
                      logoUrl: url,
                    });
                    onSuccess && onSuccess({}, new XMLHttpRequest());
                  } catch (err) {
                    onError && onError(err as any);
                  }
                }}
              >
                {(logoList?.length || 0) >= 1 ? null : (
                  <div>
                    <PlusOutlined />
                  </div>
                )}
              </Upload>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  · 권장 규격: 정사각형 PNG/JPG, 최소 400px 이상 (배경 투명 권장)
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  · 파일 크기는 2MB 이하로 업로드해주세요. 교체 시 기존 이미지는 즉시 대체됩니다.
                </Text>
              </div>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="서비스 개설비용 납부 계좌이체 여부" name="serviceFeePayment">
              <Select>
                <Select.Option value="paid">납부완료</Select.Option>
                <Select.Option value="unpaid">미납부</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="서비스 개설비용 납부 계좌이체 납부일"
              name="serviceFeePaymentDate"
              dependencies={['serviceFeePayment']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (getFieldValue('serviceFeePayment') === 'paid' && !value) {
                      return Promise.reject(new Error('납부완료 선택 시 납부일은 필수입니다.'));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={onlyTodayDisabledDate}
                allowClear={false}
                inputReadOnly
                disabled={isPaymentDateLocked}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="가입 신청일" name="applicationDate">
              <DatePicker style={{ width: '100%' }} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="가입 승인일" name="registrationDate">
              <DatePicker style={{ width: '100%' }} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="탈퇴일" name="withdrawalDate">
              <DatePicker style={{ width: '100%' }} disabled placeholder="탈퇴 처리 시 자동 입력" />
            </Form.Item>
          </Col>
        </Row>

        {/* 버튼 */}
        <div style={{ textAlign: 'right', marginTop: 32 }}>
          <Space size="middle">
            <Button 
              danger 
              size="large"
              style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              onClick={handleWithdraw}
            >
              탈퇴처리
            </Button>
            <Button 
              type="primary" 
              size="large"
              style={{ height: 48, fontSize: 16, padding: '0 32px' }}
              onClick={handleSave}
            >
              저장하기
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default BasicInfo; 